/**
 * @module features/entity/entity-metadata
 * @description Entity metadata display functions for all entity types
 */

import { formatDate, formatFileSize } from '../../utils/formatters.js';
import { escapeHtml } from '../../utils/html-utils.js';
import { getEntityInfo } from '../../core/router/url-router.js';
import { getDOMElements } from '../../ui/dom/dom-manager.js';
import { displayLinkedEntities } from './linked-entities-display.js';
import { logger } from '../../utils/logger.js';

/**
 * Display entity metadata (entity-aware version)
 * Handles all four entity types with their specific fields
 *
 * @param {Object} metadata - Entity metadata object from API
 * @param {string} entityType - Type of entity (projects, opportunities, leads, contacts)
 * @param {Object} config - Entity type configuration from ENTITY_TYPES
 */
export function displayEntityMetadata(metadata, entityType, config) {
    const dom = getDOMElements;

    // Get entity ID and name from the API response
    const entityId = metadata.entityId || 'Unknown';
    const entityName = metadata.entityName || 'Untitled';

    // Create entity-specific link — suppress when deleted so the defunct record isn't reachable
    const entityLink = (entityId !== 'Unknown' && metadata.isDeleted !== true)
        ? `<a href="${config.insightlyUrl}${escapeHtml(entityId)}" target="_blank" class="entity-link">${escapeHtml(entityId)}</a>`
        : escapeHtml(entityId);

    // Build metadata HTML - use custom labels if provided
    const metadataTitle = config.metadataTitle || `${config.label} Information`;
    const currentLabel = config.metadataCurrentLabel || `Current ${config.label}`;
    const idLabel = config.metadataIdLabel || `Insightly ${config.label} ID`;

    let metadataHTML = '';

    metadataHTML += `<h3>${config.icon} ${metadataTitle}</h3>`;

    // Show a warning banner when the entity has been deleted in Insightly.
    // Placed after the h3 so the title visually separates it from the
    // absolutely-positioned Upload Files button in the top-right corner.
    // Guards with strict equality so the banner is suppressed when isDeleted
    // is absent (undefined) or any other falsy value — backwards-compatible.
    if (metadata.isDeleted === true) {
        metadataHTML += `
            <div class="deleted-banner" role="alert">
                <span class="deleted-banner-icon">⚠️</span>
                <span>This ${config.label.toLowerCase()} has been deleted in Insightly. Archived files are still accessible below.</span>
            </div>`;
    }

    // Add SUDB ID only for projects and opportunities (not for leads/contacts)
    if (config.hasSudbId && metadata.sudbId) {
        const sudbId = metadata.sudbId || 'Unknown';
        const sudbLink = sudbId !== 'Unknown'
            ? `<a href="https://orders.shuttersup.co.uk/Order/DatabaseId/${escapeHtml(sudbId)}" target="_blank" class="sudb-link">${escapeHtml(sudbId)}</a>`
            : escapeHtml(sudbId);
        metadataHTML += `<p class="sudb-id-display"><strong>SUDB ID:</strong> ${sudbLink}</p>`;
    }

    metadataHTML += `
        <p class="entity-name-display"><strong>${currentLabel}:</strong> <span class="entity-name-value">${escapeHtml(entityName)}</span></p>
        <p><strong>${idLabel}:</strong> ${entityLink}</p>
        <p><strong>Created:</strong> ${formatDate(metadata.created)}</p>
        <p><strong>Files:</strong> ${metadata.fileCount || 0}</p>
        <p><strong>Total Size:</strong> ${formatFileSize(metadata.totalSize)}</p>
    `;

    dom.fileMetadata.innerHTML = metadataHTML;

    // Add upload button after metadata is displayed
    addUploadButtonToMetadata(entityType, entityId);

    // Display linked entities if they exist
    const linkedEntitiesContainer = document.getElementById('linked-entities-container');
    if (linkedEntitiesContainer && metadata.linkedEntities) {
        displayLinkedEntities(metadata.linkedEntities, linkedEntitiesContainer);
    }
}

/**
 * Display metadata (legacy version for backward compatibility)
 * This is the original project-only version
 *
 * @param {Object} metadata - Project metadata object
 * @deprecated Use displayEntityMetadata instead
 */
export function displayMetadata(metadata) {
    const dom = getDOMElements;

    const sudbId = metadata.sudbId || 'Unknown';
    const projectId = metadata.projectId || 'Unknown';
    const projectName = metadata.projectName || 'Untitled';

    // Create SUDB ID link
    const sudbLink = sudbId !== 'Unknown'
        ? `<a href="https://orders.shuttersup.co.uk/Order/DatabaseId/${escapeHtml(sudbId)}" target="_blank" class="sudb-link">${escapeHtml(sudbId)}</a>`
        : escapeHtml(sudbId);

    // Create Insightly Project ID link
    const projectLink = projectId !== 'Unknown'
        ? `<a href="https://crm.na1.insightly.com/details/project/${escapeHtml(projectId)}" target="_blank" class="project-link">${escapeHtml(projectId)}</a>`
        : escapeHtml(projectId);

    dom.fileMetadata.innerHTML = `
        <h3>Project Information</h3>
        <p class="sudb-id-display"><strong>SUDB ID:</strong> ${sudbLink}</p>
        <p class="project-name-display"><strong>Current Project:</strong> <span class="project-name-value">${escapeHtml(projectName)}</span></p>
        <p><strong>Insightly Project ID:</strong> ${projectLink}</p>
        <p><strong>Created:</strong> ${formatDate(metadata.created)}</p>
        <p><strong>Files:</strong> ${metadata.fileCount || 0}</p>
        <p><strong>Total Size:</strong> ${formatFileSize(metadata.totalSize)}</p>
    `;

    // Add upload button after metadata is displayed
    addUploadButtonToMetadata();
}

/**
 * Add upload button to metadata section (entity-aware)
 *
 * @param {string} entityType - Entity type (defaults to 'projects')
 * @param {string|null} entityId - Entity ID (will auto-detect from URL if not provided)
 */
export function addUploadButtonToMetadata(entityType = 'projects', entityId = null) {
    if (!entityId) {
        // Try to get from current context
        const entityInfo = getEntityInfo();
        if (entityInfo) {
            entityType = entityInfo.type;
            entityId = entityInfo.id;
        } else {
            return;
        }
    }

    // Check if button already exists
    if (document.getElementById('upload-files-btn')) return;

    const uploadBtn = document.createElement('button');
    uploadBtn.id = 'upload-files-btn';
    uploadBtn.className = 'upload-files-btn';
    uploadBtn.innerHTML = '<span>📤</span><span>Upload Files</span>';

    // Import showUploadModal dynamically to avoid circular dependency
    uploadBtn.addEventListener('click', () => {
        // Access global showUploadModal function (will be defined by upload module)
        if (typeof window.showUploadModal === 'function') {
            // Get current user from the app
            const currentUser = window.__app?.getCurrentUser?.();
            if (currentUser) {
                window.showUploadModal(entityType, entityId, currentUser);
            } else {
                logger.error('User not authenticated');
            }
        } else {
            logger.error('Upload modal function not available');
        }
    });

    // Add button to metadata section
    const metadataSection = document.getElementById('file-metadata');
    if (metadataSection) {
        metadataSection.appendChild(uploadBtn);
    }
}

/**
 * Clear metadata display
 */
export function clearMetadata() {
    const dom = getDOMElements;
    dom.fileMetadata.innerHTML = '';

    // Remove upload button if it exists
    const uploadBtn = document.getElementById('upload-files-btn');
    if (uploadBtn) {
        uploadBtn.remove();
    }

    // Clear linked entities container
    const linkedEntitiesContainer = document.getElementById('linked-entities-container');
    if (linkedEntitiesContainer) {
        linkedEntitiesContainer.innerHTML = '';
        linkedEntitiesContainer.classList.add('hidden');
    }
}

/**
 * Update entity name in metadata display
 * @param {string} newName - New entity name
 */
export function updateEntityName(newName) {
    const entityNameElement = document.querySelector('.entity-name-value');
    if (entityNameElement) {
        entityNameElement.textContent = newName;
    }
}

export default {
    displayEntityMetadata,
    displayMetadata,
    addUploadButtonToMetadata,
    clearMetadata,
    updateEntityName
};
